import ExcelJS from 'exceljs';
import dayjs from 'dayjs';
import { formatDate } from '@/lib/utils';
import {
  FLUXO_CAIXA_CATEGORIA_LABELS,
  FLUXO_CAIXA_CENTRO_CUSTO_LABELS,
  FLUXO_CAIXA_TIPO_LABELS,
  FluxoCaixaExportFormato,
  FluxoCaixaFiltros,
  FluxoCaixaLancamento,
  FluxoCaixaResumo,
} from '../types/fluxoCaixa';

function gerarNomeArquivo(filtros: FluxoCaixaFiltros, formato: FluxoCaixaExportFormato): string {
  const inicio = dayjs(filtros.dataInicio).format('MMM_YYYY').toLowerCase();
  const fim = dayjs(filtros.dataFim).format('MMM_YYYY').toLowerCase();
  const sufixo = inicio === fim ? inicio : `${inicio}_${fim}`;
  return `fluxo_caixa_${sufixo}.${formato}`;
}

function linhasParaExportacao(lancamentos: FluxoCaixaLancamento[]) {
  return lancamentos.map(l => ({
    data: formatDate(l.data_lancamento),
    tipo: FLUXO_CAIXA_TIPO_LABELS[l.lancamento_tipo],
    categoria: FLUXO_CAIXA_CATEGORIA_LABELS[l.categoria],
    centroCusto: l.centro_custo ? FLUXO_CAIXA_CENTRO_CUSTO_LABELS[l.centro_custo] : '',
    descricao: l.descricao ?? '',
    documento: l.documento ?? '',
    valor: l.valor,
  }));
}

function downloadBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  window.URL.revokeObjectURL(url);
}

export async function exportarFluxoCaixa(
  lancamentos: FluxoCaixaLancamento[],
  resumo: FluxoCaixaResumo,
  filtros: FluxoCaixaFiltros,
  formato: FluxoCaixaExportFormato
): Promise<void> {
  const linhas = linhasParaExportacao(lancamentos);
  const filename = gerarNomeArquivo(filtros, formato);

  if (formato === 'csv') {
    const headers = ['Data', 'Tipo', 'Categoria', 'Centro de Custo', 'Descrição', 'Documento', 'Valor'];
    const csvLinhas = linhas.map(l =>
      [l.data, l.tipo, l.categoria, l.centroCusto, l.descricao, l.documento, l.valor.toFixed(2)]
        .map(v => `"${String(v).replace(/"/g, '""')}"`)
        .join(';')
    );
    csvLinhas.push('');
    csvLinhas.push('"RESUMO"');
    csvLinhas.push(`"Total Entradas";"${resumo.total_entrada.toFixed(2)}"`);
    csvLinhas.push(`"Total Saídas";"${resumo.total_saida.toFixed(2)}"`);
    csvLinhas.push(`"Saldo";"${resumo.saldo.toFixed(2)}"`);

    const blob = new Blob([`\uFEFF${[headers.join(';'), ...csvLinhas].join('\n')}`], {
      type: 'text/csv;charset=utf-8',
    });
    downloadBlob(blob, filename);
    return;
  }

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Fluxo de Caixa');

  worksheet.columns = [
    { header: 'Data', key: 'data', width: 14 },
    { header: 'Tipo', key: 'tipo', width: 12 },
    { header: 'Categoria', key: 'categoria', width: 22 },
    { header: 'Centro de Custo', key: 'centroCusto', width: 18 },
    { header: 'Descrição', key: 'descricao', width: 32 },
    { header: 'Documento', key: 'documento', width: 18 },
    { header: 'Valor (R$)', key: 'valor', width: 16 },
  ];

  linhas.forEach(l => worksheet.addRow(l));

  const resumoRow = worksheet.rowCount + 2;
  worksheet.getCell(`A${resumoRow}`).value = 'RESUMO';
  worksheet.getCell(`A${resumoRow}`).font = { bold: true };
  worksheet.getCell(`A${resumoRow + 1}`).value = 'Total Entradas';
  worksheet.getCell(`B${resumoRow + 1}`).value = resumo.total_entrada;
  worksheet.getCell(`A${resumoRow + 2}`).value = 'Total Saídas';
  worksheet.getCell(`B${resumoRow + 2}`).value = resumo.total_saida;
  worksheet.getCell(`A${resumoRow + 3}`).value = 'Saldo';
  worksheet.getCell(`B${resumoRow + 3}`).value = resumo.saldo;

  worksheet.getColumn('valor').numFmt = '#,##0.00';
  worksheet.getCell(`B${resumoRow + 1}`).numFmt = '#,##0.00';
  worksheet.getCell(`B${resumoRow + 2}`).numFmt = '#,##0.00';
  worksheet.getCell(`B${resumoRow + 3}`).numFmt = '#,##0.00';

  const buffer = await workbook.xlsx.writeBuffer();
  downloadBlob(
    new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
    filename
  );
}

export function formatarPeriodoExportacao(filtros: FluxoCaixaFiltros): string {
  return `${formatDate(filtros.dataInicio)} — ${formatDate(filtros.dataFim)}`;
}
